import React, { useCallback, useRef, useState } from "react";
import ModalWidget from "../ModalWidget";
import { ReactComponent as ImportBorderSvg } from "../../../assets/svg/import.svg";
import { capitalizeFirstLetter } from "../../../services/HelperService";

interface ImportFileUploadProps {
    userType: string;
    onBack: () => void;
    onClose?: () => void;
    onFileUploaded: (data: string[][], headers: string[]) => void;
}

function ImportFileUpload({ userType, onBack, onClose, onFileUploaded }: ImportFileUploadProps) {
    const capitalWord = capitalizeFirstLetter(userType);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dragOver, setDragOver] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [fileName, setFileName] = useState<string | null>(null);
    const [rowCount, setRowCount] = useState<number>(0);
    const [columnCount, setColumnCount] = useState<number>(0);
    const [parsedData, setParsedData] = useState<string[][] | null>(null);
    const [parsedHeaders, setParsedHeaders] = useState<string[] | null>(null);
    const [parsing, setParsing] = useState(false);

    const parseFile = useCallback((file: File) => {
        setError(null);
        setFileName(file.name);
        setParsing(true);
        const ext = file.name.split(".").pop()?.toLowerCase();

        if (ext === "csv") {
            void import("papaparse").then(({ default: Papa }) => {
                Papa.parse(file, {
                    skipEmptyLines: true,
                    complete: (results) => {
                        setParsing(false);
                        const data = results.data as string[][];
                        if (data.length < 2) {
                            setError("File must contain at least a header row and one data row.");
                            return;
                        }
                        const headers = data[0];
                        const rows = data.slice(1).filter(row => row.some(cell => cell && cell.trim() !== ""));
                        setParsedHeaders(headers);
                        setParsedData(rows);
                        setRowCount(rows.length);
                        setColumnCount(headers.length);
                    },
                    error: () => {
                        setParsing(false);
                        setError("Failed to parse CSV file.");
                    },
                });
            }).catch(() => {
                setParsing(false);
                setError("Failed to load CSV parser.");
            });
        } else if (ext === "xlsx" || ext === "xls") {
            const reader = new FileReader();
            reader.onload = (e) => {
                void import("xlsx").then((XLSX) => {
                    try {
                        const workbook = XLSX.read(e.target?.result, { type: "array" });
                        const sheetName = workbook.SheetNames[0];
                        const sheet = workbook.Sheets[sheetName];
                        const jsonData = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1, defval: "" });
                        if (jsonData.length < 2) {
                            setError("File must contain at least a header row and one data row.");
                            return;
                        }
                        const headers = (jsonData[0] as unknown as string[]).map(String);
                        const rows = jsonData.slice(1)
                            .map(row => (row as unknown as string[]).map(String))
                            .filter(row => row.some(cell => cell && cell.trim() !== ""));
                        setParsedHeaders(headers);
                        setParsedData(rows);
                        setRowCount(rows.length);
                        setColumnCount(headers.length);
                    } catch {
                        setError("Failed to parse Excel file.");
                    } finally {
                        setParsing(false);
                    }
                }).catch(() => {
                    setParsing(false);
                    setError("Failed to load Excel parser.");
                });
            };
            reader.onerror = () => {
                setParsing(false);
                setError("Failed to read Excel file.");
            };
            reader.readAsArrayBuffer(file);
        } else {
            setParsing(false);
            setError("Unsupported file type. Please upload a .csv, .xlsx, or .xls file.");
        }
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) parseFile(file);
    }, [parseFile]);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = () => setDragOver(false);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) parseFile(file);
    };

    const handleContinue = () => {
        if (parsedData && parsedHeaders) {
            onFileUploaded(parsedData, parsedHeaders);
        }
    };

    return (
        <ModalWidget
            headerIcon={ImportBorderSvg}
            show={true}
            onHide={onBack}
            onBack={onBack}
            title={`Import ${capitalWord}s`}
            description={`Upload a CSV or Excel file with ${userType} data`}
            actionTitle="Continue"
            onAction={handleContinue}
            actionDisabled={!parsedData || parsing}
            navigateBack={false}
            backButtonTitle="Change Method"
        >
            <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
                style={{
                    border: `2px dashed ${dragOver ? "#4f46e5" : "#d1d5db"}`,
                    borderRadius: 8,
                    padding: "40px 20px",
                    textAlign: "center",
                    cursor: "pointer",
                    backgroundColor: dragOver ? "#f5f3ff" : "#fafafa",
                    transition: "all 0.2s",
                }}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    style={{ display: "none" }}
                    onChange={handleFileSelect}
                />
                <p className="font-bold mb-1">
                    {parsing ? "Parsing file…" : fileName ? fileName : "Drag & drop a file here or click to browse"}
                </p>
                <p className="text-muted font-xs mb-0">
                    Supports .csv, .xlsx, .xls
                </p>
            </div>

            {error && (
                <div className="alert alert-danger mt-3 py-2 font-xs">{error}</div>
            )}

            {parsedData && (
                <div className="mt-3 p-3" style={{ backgroundColor: "#f0fdf4", borderRadius: 8 }}>
                    <p className="mb-1 font-bold font-sm">File parsed successfully</p>
                    <p className="mb-0 font-xs text-muted">
                        {rowCount} row{rowCount !== 1 ? "s" : ""} &middot; {columnCount} column{columnCount !== 1 ? "s" : ""} detected
                    </p>
                </div>
            )}
        </ModalWidget>
    );
}

export default ImportFileUpload;
